import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, Lock, PartyPopper } from "lucide-react";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

export default function ClientJourney() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);

  const load = () => api.get("/journey").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const complete = async (key) => {
    try {
      const r = await api.post(`/journey/${key}/complete`);
      setData(r.data);
      toast({ title: "Step complete", description: "Progress saved." });
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    }
  };

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;

  if (!data.accepted) {
    return (
      <Card className="mx-auto max-w-xl text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100"><Lock className="h-6 w-6 text-slate-400" /></div>
        <h2 className="mt-4 text-lg font-bold text-slate-900">No active client journey’</h2>
        <p className="mt-2 text-sm text-slate-500">Accept an opportunity in the Opportunity Center to begin your client journey.</p>
        <Button onClick={() => navigate("/opportunities")} className="btn-primary-gradient mt-6 border-0 text-white">Go to Opportunity Center</Button>
      </Card>
    );
  }

  const firstOpen = data.steps.find((s) => !s.done);

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Client Journey — {data.opportunity?.client_name}</h2>
            <p className="text-sm text-slate-500">{data.opportunity?.role_title} · ${data.opportunity?.pay_rate}/hr</p>
          </div>
          {data.client_ready && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700"><PartyPopper className="h-4 w-4" /> Client Ready</span>
          )}
        </div>
      </Card>

      <div className="space-y-3">
        {data.steps.map((s, i) => {
          const isNext = firstOpen && s.key === firstOpen.key;
          return (
            <Card key={s.key} className={isNext ? "ring-2 ring-violet-200" : ""}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  {s.done ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-500" /> : isNext ? <Circle className="mt-0.5 h-5 w-5 text-violet-500" /> : <Lock className="mt-0.5 h-4 w-4 text-slate-300" />}
                  <div>
                    <div className={`font-semibold ${s.done || isNext ? "text-slate-900" : "text-slate-400"}`}>{i + 1}. {s.label}</div>
                    <div className="text-sm text-slate-500">{s.desc}</div>
                  </div>
                </div>
                {!s.done && isNext && (
                  <Button size="sm" onClick={() => complete(s.key)} className="btn-primary-gradient border-0 text-white">
                    {s.key === "client_ready" ? "Finish" : "Complete"}
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
